import { Link } from 'react-router-dom';
import { BedDouble, CheckCircle2, Eye } from 'lucide-react';
import { PO_STATUS_ORDER, isStepReached, formatDate } from './badgeUtils';

/* flattens purchase-order items into a single project-wide, room-groupable list */
export function flattenPurchaseOrderItems(purchaseOrders) {
  const flat = [];
  for (const po of purchaseOrders) {
    const items = Array.isArray(po.items) ? po.items : [];
    for (const item of items) {
      flat.push({
        ...item,
        poId: po.poId,
        poDate: po.date,
        orderNumber: po.orderNumber,
        vendorName: po.vendorName,
      });
    }
  }
  return flat;
}

function normalizeKey(v) {
  return String(v || '').trim().toLowerCase();
}

/* finds the unified item (with invoice/specs info) matching a PO line item.
   PO items, invoice line items, and proposal line items each get their own
   per-document itemId, so `itemId` rarely lines up across documents — the
   stable cross-document key is `productItemId`. */
export function findMatchingItem(items, poItem) {
  if (poItem.productItemId) {
    const byProductId = items.find((it) => it.productItemId === poItem.productItemId);
    if (byProductId) return byProductId;
  }
  if (poItem.itemId) {
    const byId = items.find((it) => it.itemId === poItem.itemId);
    if (byId) return byId;
  }

  const name = normalizeKey(poItem.name);
  // unified items default an empty room to 'Unspecified' (see shapeUnifiedItem
  // in clientPortal.js); PO items just leave it '' — normalize both the same
  // way or an unroomed PO item never matches its invoiced counterpart.
  const room = normalizeKey(poItem.room) || 'unspecified';
  const byNameAndRoom = items.find(
    (it) => normalizeKey(it.name) === name && (normalizeKey(it.room) || 'unspecified') === room
  );
  if (byNameAndRoom) return byNameAndRoom;

  // Room labels are entered independently on the PO vs. the invoice/proposal
  // and often don't line up even for the same physical item — fall back to a
  // name-only match as long as it's unambiguous.
  const byNameOnly = items.filter((it) => normalizeKey(it.name) === name);
  return byNameOnly.length === 1 ? byNameOnly[0] : null;
}

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'delivered' || s === 'installed') return 'cp-badge-green';
  if (s === 'ordered' || s === 'en route' || s === 'workroom') return 'cp-badge-blue';
  if (s === 'back ordered') return 'cp-badge-yellow';
  return 'cp-badge-gray';
}

export function StatusCard({ item, matched, projectId, onClick }) {
  const inv = matched?.invoice;
  const isPaid =
    inv && (inv.remainingBalance != null ? inv.remainingBalance <= 0 : inv.status === 'paid');
  const specs = matched?.specs || [];
  const imageUrl = item.imageUrl || matched?.imageUrl;
  // PO items only carry the vendor product name (vendors need it); the matched
  // invoice/proposal item's name already resolves the client-facing override.
  const name = matched?.name || item.name;

  return (
    <div
      className={`cp-status-card${onClick ? ' clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick();
            }
          : undefined
      }
    >
      <div className="cp-item-img">{imageUrl ? <img src={imageUrl} alt="" /> : null}</div>

      <div className="cp-item-info">
        <p className="cp-item-name">{name || '—'}</p>
        {specs.length > 0 && (
          <ul className="cp-item-specs">
            {specs.map((spec, i) => (
              <li key={i}>{spec}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="cp-status-meta">
        <div className="cp-status-meta-row">
          <span className="cp-status-meta-label">QTY</span>
          <span className="cp-status-meta-value">{item.quantity ?? matched?.qty ?? '—'}</span>
        </div>
        <div className="cp-status-meta-row">
          <span className="cp-status-meta-label">Status:</span>
          <span className={`cp-badge ${statusBadgeClass(item.status)}`}>
            {item.status || 'No status'}
          </span>
        </div>
        {item.poDate && (
          <div className="cp-status-meta-row">
            <span className="cp-status-meta-label">Date:</span>
            <span className="cp-status-meta-value">{formatDate(item.poDate)}</span>
          </div>
        )}
        <div className="cp-status-track">
          {PO_STATUS_ORDER.map((step) => (
            <div
              key={step}
              className={`cp-po-status-step ${isStepReached(item.status, step) ? 'reached' : ''} ${
                item.status === step ? 'active' : ''
              }`}
              title={step}
            />
          ))}
        </div>
      </div>

      <div className="cp-status-illustration">
        <BedDouble size={40} strokeWidth={1.25} />
      </div>

      <div className="cp-item-invoice">
        {inv ? (
          <>
            <span className={`cp-badge ${isPaid ? 'cp-badge-green' : 'cp-badge-yellow'}`}>
              {isPaid ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle2 size={12} /> Invoiced
                </span>
              ) : (
                'Invoiced'
              )}
            </span>
            <p className="cp-item-invoice-meta">
              Invoice #{inv.invoiceNumber || inv.invoiceId}
              {inv.invoiceDate && (
                <>
                  <br />
                  Issued on {formatDate(inv.invoiceDate)}
                </>
              )}
            </p>
            <Link
              to={`/client/projects/${projectId}/invoices/${inv.invoiceId}`}
              className="cp-btn-primary"
              style={{ padding: '7px 16px', fontSize: 13 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Eye size={13} /> View
            </Link>
          </>
        ) : (
          <span className="cp-badge cp-badge-gray">Not Invoiced</span>
        )}
      </div>
    </div>
  );
}
